import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { PopUpNotificationComponent } from '@components/pop-up-notification/pop-up-notification.component';
import { ScreenSizeType } from '@models/responsive';
import { AlarmAnimationService } from '@service/alarm-animation.service';
import { ResponsiveService } from '@service/responsive.service';
import { Channel } from 'ops/common/models/channel';
import { Subscription } from 'rxjs';
import { PageAccessType } from 'ops/common/models/access-settings';
import { CustomGroupAccessSettingsService } from '@service/custom-group-access-settings.service';
import * as _ from 'lodash';
import { AuthService } from '@service/auth.service';
import { ChannelTableRowWrapComponent } from '@components/channel-table-row-wrap/channel-table-row-wrap.component';

const channelTableRowMap = new Map();
channelTableRowMap.set(ScreenSizeType.XLarge, 66);
channelTableRowMap.set(ScreenSizeType.Large, 170);
channelTableRowMap.set(ScreenSizeType.Medium, 170);
channelTableRowMap.set(ScreenSizeType.Small, 250);
channelTableRowMap.set(ScreenSizeType.XSmall, 250);

@Component({
    selector: 'app-ops-channel-list',
    templateUrl: './ops-channel-list.component.html',
    styleUrls: ['./ops-channel-list.component.scss'],
    providers: [AlarmAnimationService],
    standalone: false,
})
export class OpsChannelListComponent implements OnChanges, OnDestroy {
    readonly viewContianerHeader = 65;

    @Output() onNavigateBack = new EventEmitter();
    @Output() onChannelSelected = new EventEmitter<Channel>();

    @Input() title = '';
    @Input() description = '';
    @Input() channels: Channel[];
    @Input() customGroup: Boolean;
    @Input() preview: boolean = false;
    @Input() groupId: string;
    @Input() publicAccess: Boolean;
    @Input() selectedChannel: Channel;

    @Output() onEditGroup = new EventEmitter();
    @Output() onDeleteGroup = new EventEmitter();

    @Output() onSubmit = new EventEmitter<Channel>();
    @Output() onAdvancedSettings = new EventEmitter<Channel>();
    @Output() onDeleteAlarm = new EventEmitter<Channel>();

    @ViewChild('alarmFlashParent', { static: true })
    alarmFlashParent: ElementRef;

    @ViewChild('mainContent', { static: true })
    mainContent: ElementRef;

    @ViewChild('channelTableRowWrap') channelTableRowWrap: ChannelTableRowWrapComponent;

    @ViewChild('previewMessage', { static: true })
    previewMessage: PopUpNotificationComponent;

    @ViewChild('scrollContainer') scrollContainer: ElementRef;

    previewPop = false;

    confirmDelete: Boolean = false;
    selectedChannelIndex = null;
    showSimpleFilter = new Set([
        ScreenSizeType.Large,
        ScreenSizeType.Medium,
        ScreenSizeType.Small,
        ScreenSizeType.XSmall,
    ]);
    filterOption = {
        channel: '',
    };

    simpleFilterLabel = {
        placeholder: 'Search Channel Name or Number',
        model: 'channel',
    };

    tableHeight = 0;
    tableRowHeight = 0;
    tableOffset = 0;
    start: number = 0;
    end: number = 0;
    scrollYInit: number = 0;

    pageAccessTypeTile: string = 'page-access.type';
    pageAccessType: PageAccessType;
    pageAccessIcon: string;
    shareSettingsOpened: boolean = false;
    usersAccess;

    currentUserSubjectSubscription: Subscription;

    public filtredChannels: Channel[] = [];

    constructor(
        public responsive: ResponsiveService,
        private cdr: ChangeDetectorRef,
        private customGroupAccessService: CustomGroupAccessSettingsService,
        private authService: AuthService,
    ) {}

    async ngOnInit() {
        if (this.customGroup) {
            this.usersAccess = await this.customGroupAccessService.getAll(this.groupId);
        }

        this.currentUserSubjectSubscription = this.authService.currentUser$.subscribe(async () => {
            this.shareSettingsOpened = false;
        });

        this.dataFilter();
        this.updatePageAccessType();

        this.cdr.markForCheck();
    }

    async ngOnChanges(changes: SimpleChanges) {
        if (changes.channels) {
            this.dataFilter();
        }
        if (changes.publicAccess) {
            this.cdr.markForCheck();
        }
        if (changes.groupId) {
            this.usersAccess = await this.customGroupAccessService.getAll(this.groupId);
        }
        this.updatePageAccessType();
        this.cdr.markForCheck();
    }

    ngOnDestroy(): void {
        this.currentUserSubjectSubscription?.unsubscribe();
    }

    handleInitScroll(element: HTMLElement) {
        if (element) {
            element.scrollIntoView({ block: 'nearest' });
        }
    }

    public dataFilter() {
        let newAlarmData = this.channels;

        newAlarmData = newAlarmData.filter((data: Channel) =>
            this.filterOption.channel !== ''
                ? data.opsData.name
                      .toUpperCase()
                      .includes(this.filterOption.channel.toUpperCase()) ||
                  data.opsData.tag.includes(this.filterOption.channel)
                : data,
        );

        this.filtredChannels = newAlarmData;
        const latestChannelsMap = _.keyBy(newAlarmData, 'id');
        this.filtredChannels = _.values(latestChannelsMap);
    }

    onFilterChange(event) {
        this.filterOption = { ...this.filterOption, ...event };
        this.dataFilter();
    }

    closePreviewForm() {
        this.channelTableRowWrap.closePreviewForm();
    }

    emitNavigateBack() {
        this.channels = null;
        this.onNavigateBack.emit();
    }

    onDelete() {
        if (this.preview) {
            this.showPreviewMsg();
        } else {
            this.onDeleteGroup.emit();
        }
    }

    onEdit() {
        this.onEditGroup.emit(this.channels);
        this.channels = null;
        this.cdr.markForCheck();
    }

    emitChannelSelected(channel: Channel, i) {
        if (this.responsive.isXLarge()) {
            this.onChannelSelected.emit(channel);
        } else {
            this.selectedChannel = channel;
            this.selectedChannelIndex = i;
        }
    }

    onSubmitEmit(event: Channel) {
        if (this.preview) {
            this.showPreviewMsg();
        } else {
            this.onSubmit.emit(event);
        }
    }

    onCloseClicked() {
        this.selectedChannel = null;
        this.selectedChannelIndex = null;
    }

    onDeleteEmit(event) {
        if (this.preview) {
            this.showPreviewMsg();
        } else {
            this.onDeleteAlarm.emit(event);
        }
    }

    onAdvancedSettingsEmit(event: Channel) {
        if (this.preview) {
            this.showPreviewMsg();
        } else {
            this.onAdvancedSettings.emit(event);
        }
    }

    showPreviewMsg() {
        this.previewMessage.setVisible(true);
    }

    getPreviewMessageVisible() {
        return this.previewMessage.getVisible();
    }

    isScrollBtnVisible() {
        if (this.scrollContainer) {
            const rect = this.scrollContainer.nativeElement.getBoundingClientRect();
            const mainContentRect = this.mainContent.nativeElement.getBoundingClientRect();
            const topShown = mainContentRect.height + mainContentRect.y < rect.top;
            if (!topShown) {
                return (
                    this.responsive.isXSmall() ||
                    this.responsive.isHandset() ||
                    this.responsive.isTabletPortrait()
                );
            }
        }
        return false;
    }

    onScrollToTop() {
        if (this.scrollContainer) {
            this.scrollContainer.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
        }
    }

    async updateUsersAccess(updatedUsersAccess) {
        await this.customGroupAccessService.update([...updatedUsersAccess]);
    }

    async updatePageAccess(pageAccess) {
        await this.customGroupAccessService.updatePageAccess(pageAccess, this.groupId);
        this.updatePageAccessType();
    }

    updatePageAccessType() {
        if (this.publicAccess) {
            this.pageAccessType = PageAccessType.Public;
            this.pageAccessIcon = 'lock-open';
        } else {
            this.pageAccessType = PageAccessType.Private;
            this.pageAccessIcon = 'lock-closed';
        }
    }

    changeShareSettings(e: MouseEvent) {
        this.shareSettingsOpened = !this.shareSettingsOpened;
        e.stopPropagation();
    }
}
